import { BaseSyntheticEvent, useMemo } from 'react';

import { FormProvider, useForm, UseFormProps } from 'react-hook-form';

import {
  Alert,
  Box,
  Button,
  Flex,
  Heading,
  Loading,
  Stack,
  Text,
} from '@brand/components';
import merge from 'lodash.merge';
import { IField, IFormSchema, IFormStyles } from 'schema/generator/FormSchema';

import { StyleCtx } from 'hooks/form/useStyles';

import AddressField from 'components/molecules/form/AddressField';
import CheckboxField from 'components/molecules/form/CheckboxField';
import DateField from 'components/molecules/form/DateField';
import DateTimeField from 'components/molecules/form/DateTimeField';
import { MultiSelectField } from 'components/molecules/form/MultiSelectField';
import NumberField from 'components/molecules/form/NumberField';
import RadioField from 'components/molecules/form/RadioField';
import { SelectField } from 'components/molecules/form/SelectField';
import TextField from 'components/molecules/form/TextField';
import TimeField from 'components/molecules/form/TimeField';
import UploadFileField from 'components/molecules/form/UploadFileField';
import LayoutSubheader from 'components/molecules/layouts/Subheader';
import ComponentIsVisible from 'components/utils/IsVisible';

import { defaultStyles } from './styles';

export interface IFormGeneratorProps {
  buttons?: {
    cancel?: {
      isDisabled?: boolean;
      onClick?: () => void;
      text?: string;
    };
    submit: {
      isDisabled?: boolean;
      isLoading?: boolean;
      onClick?: () => void;
      text?: string;
    };
  };
  formOptions?: UseFormProps;
  handleSubmit: (values: any, e?: BaseSyntheticEvent) => void;
  overwriteDefaultStyles?: boolean;
  requestErrorMessage?: string;
  schema: IFormSchema;
  styles?: IFormStyles;
  title?: string;
}

const renderField = ([name, field]: [string, IField]) => {
  let Component: any = <></>;

  switch (field.type) {
    case 'text':
      Component = TextField;
      break;

    // case 'textArea':
    //   Component = TextAreaField;
    //   break;

    case 'date':
      Component = DateField;
      break;

    case 'time':
      Component = TimeField;
      break;

    case 'datetime-local':
      Component = DateTimeField;
      break;

    case 'number':
      Component = NumberField;
      break;

    // case 'array':
    //   Component = ArrayField;
    //   break;

    // case 'object':
    //   Component = ObjectField;
    //   break;

    // case 'switch':
    //   Component = SwitchField;
    //   break;

    case 'checkbox':
      Component = CheckboxField;
      break;

    case 'radio':
      Component = RadioField;
      break;

    case 'select':
      Component = SelectField;
      break;

    case 'multiSelect':
      Component = MultiSelectField;
      break;

    case 'address':
      Component = AddressField;
      break;

    case 'custom':
      Component = field.component;
      return (
        <Box key={`${name}-container`}>
          <Component
            field={field}
            name={name}
            value={field.value}
            {...field.props}
          />
        </Box>
      );
    case 'subHeader':
      return (
        <Box key={`${name}-container`}>
          <LayoutSubheader
            backgroundColor={field.backgroundColor}
            iconName={field.icon}
            title={field.subHeaderTitle}
            withIcon
          />
        </Box>
      );

    case 'subHeader2':
      return (
        <Flex alignItems="center" height={8} key={`${name}-control`}>
          <Flex alignItems="center" bgColor="heading" height="1px" width="100%">
            <Text bgColor="white" fontWeight="medium" ml={8} px={4}>
              {field.title}
            </Text>
          </Flex>
        </Flex>
      );

    case 'uploadFile':
      Component = UploadFileField;
      break;

    default:
      break;
  }
  return (
    <Box key={`${name}-container`}>
      <Component field={field} name={name} />
    </Box>
  );
};

const FormGenerator = ({
  title,
  schema,
  handleSubmit,
  requestErrorMessage,
  formOptions,
  overwriteDefaultStyles,
  buttons,
  styles = {},
}: IFormGeneratorProps): JSX.Element => {
  const form = useForm(formOptions);

  const baseStyles = useMemo(() => {
    return overwriteDefaultStyles ? styles : merge(defaultStyles, styles);
  }, [styles, overwriteDefaultStyles]);

  return (
    <StyleCtx.Provider value={baseStyles}>
      <FormProvider {...form}>
        <Box
          as="form"
          onSubmit={form.handleSubmit(handleSubmit)}
          {...baseStyles.form?.container}
        >
          <ComponentIsVisible when={!!title}>
            <Heading>{title}</Heading>
          </ComponentIsVisible>
          <Stack spacing={4}>{Object.entries(schema).map(renderField)}</Stack>
          <ComponentIsVisible when={!!buttons}>
            <Flex
              gridTemplateColumns={buttons?.cancel ? '35% 65%' : '1fr'}
              {...baseStyles.form?.buttonGroup}
            >
              <ComponentIsVisible when={!!buttons?.cancel}>
                <Button
                  disabled={buttons?.cancel?.isDisabled}
                  onClick={buttons?.cancel?.onClick}
                  type="button"
                  variant="outlineBlue"
                  {...baseStyles.form?.cancelButton}
                >
                  {buttons?.cancel?.text || 'Cancel'}
                </Button>
              </ComponentIsVisible>
              <Button
                disabled={buttons?.submit?.isDisabled}
                onClick={buttons?.submit?.onClick}
                type="submit"
                {...baseStyles.form?.submitButton}
              >
                <ComponentIsVisible when={!buttons?.submit?.isLoading}>
                  {buttons?.submit?.text || 'Submit'}
                </ComponentIsVisible>
                <ComponentIsVisible when={!!buttons?.submit?.isLoading}>
                  <Loading size={5} />
                </ComponentIsVisible>
              </Button>
            </Flex>
          </ComponentIsVisible>

          <ComponentIsVisible when={!!requestErrorMessage}>
            <Alert
              borderRadius={8}
              color="red.400"
              m="0 auto"
              maxW="fit-content"
              status="error"
            >
              {requestErrorMessage}
            </Alert>
          </ComponentIsVisible>
        </Box>
      </FormProvider>
    </StyleCtx.Provider>
  );
};

export default FormGenerator;
